import { Component, HostBinding, Input, OnInit, OnDestroy } from '@angular/core';
import { NavigationService } from '../navigation.service';
import { NavigationEnd, Router } from '@angular/router';
import {ngcAnimations} from '../../animations';
import {select, Store} from '@ngrx/store';
import {takeWhile, withLatestFrom, take} from 'rxjs/operators';
import {isMenuOpened, State} from '@app/app.reducers';


@Component({
    selector   : 'ngc-nav-collapse',
    templateUrl: './nav-collapse.component.html',
    styleUrls  : ['./nav-collapse.component.scss'],
    animations : ngcAnimations
})
export class NavCollapseComponent implements OnInit, OnDestroy {
    @Input() item: any;
    @HostBinding('class') classes = 'nav-collapse nav-item';
    @HostBinding('class.open') public isOpen = false;

    private alive = true;

    constructor(
        private navigationService: NavigationService,
        private router: Router,
        private store: Store<State>,
    ) {
        // Listen for route changes
        router.events.pipe(
            takeWhile(() => this.alive),
            withLatestFrom(this.store.pipe(select(isMenuOpened)))
        ).subscribe(
            ([event, isMenuOpened]) => {
                if ( event instanceof NavigationEnd ) {
                    // Check if the url can be found in
                    // one of the children of this item
                    if ( this.isUrlInParentOrChildren(this.item, event.urlAfterRedirects) ) {
                        if (isMenuOpened) {
                            this.expand();
                        }
                    } else {
                        this.collapse();
                    }
                }
            }
        );

        // Listen for collapsing of any navigation item
        this.navigationService.onNavCollapseToggled
            .pipe(
                takeWhile(() => this.alive)
            ).subscribe(
                (clickedItem) => {
                    if ( clickedItem && clickedItem.children ) {
                        // Check if the clicked item is one
                        // of the children of this item
                        if ( this.isChildrenOf(this.item, clickedItem) ) {
                            return;
                        }

                        // Check if the url can be found in
                        // one of the children of this item
                        if ( this.isUrlInParentOrChildren(this.item, this.router.url) ) {
                            return;
                        }

                        // If the clicked item is not this item, collapse...
                        if ( this.item !== clickedItem ) {
                            this.collapse();
                        }
                    } else if (clickedItem && this.isUrlInParentOrChildren(this.item, this.router.url)) {
                        this.expand();
                    } else {
                        this.collapse();
                    }
                }
            );
    }

    /**
     * Toggle collapse
     *
     * @param ev
     */
    toggleOpen(ev) {
        ev.preventDefault();

        this.isOpen = !this.isOpen;

        // Navigation collapse toggled...
        this.navigationService.onNavCollapseToggled.emit(this.item);
        this.navigationService.onNavCollapseToggle.emit();
    }

    /**
     * Expand the collapsable navigation
     */
    expand() {
        if ( this.isOpen ) {
            return;
        }

        this.isOpen = true;
        this.navigationService.onNavCollapseToggle.emit();
    }

    /**
     * Collapse the collapsable navigation
     */
    collapse() {
        if ( !this.isOpen ) {
            return;
        }
        this.isOpen = false;
        this.navigationService.onNavCollapseToggle.emit();
    }

    /**
     * Check if the given parent has the
     * given item in one of its children
     *
     * @param parent
     * @param item
     * @return
     */
    isChildrenOf(parent, item) {
        if ( !parent.children ) {
            return false;
        }

        if ( parent.children.indexOf(item) !== -1 ) {
            return true;
        }

        for ( const children of parent.children ) {
            if ( children.children ) {
                return this.isChildrenOf(children, item);
            }
        }
    }

    /**
     * Check if the given url can be found
     * in parent or one of the given parent's children
     *
     * @param parent
     * @param url
     * @returns
     */
    isUrlInParentOrChildren(parent, url) {
        if ( !parent.children ) {
            return false;
        }

        if ( url.includes(parent.url) ) {
            return true;
        }

        for ( let i = 0; i < parent.children.length; i++ ) {
            if ( parent.children[i].children ) {
                if ( this.isUrlInParentOrChildren(parent.children[i], url) ) {
                    return true;
                }
            }

            if ( parent.children[i].url === url || url.includes(parent.children[i].url) ) {
                return true;
            }
        }

        return false;
    }

    ngOnInit() {
        this.store.pipe(
            select(isMenuOpened),
            take(1)
        ).subscribe((isMenuOpenedValue: boolean) => {
            if (isMenuOpenedValue && this.isUrlInParentOrChildren(this.item, this.router.url)) {
                this.expand();
            }
        }
        );
    }

    ngOnDestroy() {
        this.alive = false;
    }

}
